import type { MeResult, RoleInfo } from './types';

/**
 * Staff roles allowed on the web console.
 *
 * Source: docs/design/test-data-and-verification-lld.md §3.1, plus `teacher`
 * (docs/design/surface-map.md: teachers are dual-surface). Unknown roles
 * default to denied. Driver / caretaker / watchman are staff-app-only.
 *
 * A person with any overlapping allowlist role may use web (e.g. transport_head
 * who is also a driver). Parents are denied by kind, not by this set.
 */
export const WEB_CONSOLE_ROLES = new Set([
  'super_admin',
  'director_academic',
  'director_admin',
  'general_manager',
  'school_admin_manager',
  'principal',
  'vice_principal',
  'dept_head',
  'teacher',
  'academic_counselor',
  'admission_counselor',
  'admissions_head',
  'accountant',
  'hr_manager',
  'librarian',
  'transport_head',
  'fee_collector',
  'prm',
  'housekeeping_head',
  'front_desk',
]);

export const STAFF_APP_ONLY_ROLES = new Set(['driver', 'caretaker', 'watchman']);

export interface WebAccessDenial {
  code: 'parent' | 'staff_app_only';
  title: string;
  message: string;
}

/** Defense in depth: staff `/auth` no longer mints parent tokens, but a
 * leftover parent JWT (or a curl of `/parent/auth`) must not enter `/app`. */
export function parentBlockedFromWebMessage(kind: string | undefined): string | null {
  if (kind === 'parent') {
    return 'This website is for school staff. Please sign in with the parent app.';
  }
  return null;
}

export function isParentUser(user: { kind?: string } | null | undefined): boolean {
  return user?.kind === 'parent';
}

export function webAccessDenial(
  me: Pick<MeResult, 'kind' | 'roles'> | { kind: string; roles?: RoleInfo[] },
): WebAccessDenial | null {
  if (me.kind === 'parent') {
    return {
      code: 'parent',
      title: 'Use the parent app',
      message: 'This website is for school staff. Please sign in with the parent app.',
    };
  }
  const codes = (me.roles ?? []).map((role) => role.code);
  if (codes.some((code) => WEB_CONSOLE_ROLES.has(code))) return null;
  if (codes.some((code) => STAFF_APP_ONLY_ROLES.has(code))) {
    return {
      code: 'staff_app_only',
      title: 'Use the staff app',
      message: 'This website is for office staff. Gate, trip, and pickup work lives in the staff app.',
    };
  }
  return {
    code: 'staff_app_only',
    title: 'No web access for this role',
    message: 'This website is for school office staff. Sign in with an office role, or use the staff app.',
  };
}
